import { cn } from '@/lib/utils';
import { WORKSPACE_STAGES, WORKSPACE_STAGE_LABELS } from '@jobos/shared';
import type { WorkspaceCounts, WorkspaceStage } from '@jobos/shared';

/**
 * The four stages, with counts that respect the active filters.
 *
 * The counts are filter-aware by contract (Task 3): a tab bar that ignores the
 * filters contradicts the list beneath it the moment anyone filters by client.
 */

export interface StageTabsProps {
  stage: WorkspaceStage;
  onStageChange: (stage: WorkspaceStage) => void;
  counts: WorkspaceCounts | undefined;
  isLoading: boolean;
}

export function StageTabs({ stage, onStageChange, counts, isLoading }: StageTabsProps) {
  return (
    // Wraps rather than scrolls on a phone. Scrolling hid the fourth stage
    // behind an edge with no affordance, so "Paid" simply did not appear to
    // exist at 390px.
    <div className="flex flex-wrap gap-x-1 border-b" role="tablist" aria-label="Invoice stages">
      {WORKSPACE_STAGES.map((s) => {
        const active = s === stage;
        return (
          <button
            key={s}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onStageChange(s)}
            className={cn(
              'flex min-h-[44px] items-center gap-2 whitespace-nowrap border-b-2 px-3 py-2.5 text-sm transition-colors sm:px-4',
              active
                ? 'border-blue-900 font-semibold text-blue-900'
                : 'border-transparent text-gray-600 hover:text-gray-900',
            )}
          >
            {WORKSPACE_STAGE_LABELS[s]}
            <StageCount value={counts?.[s]} isLoading={isLoading} active={active} />
          </button>
        );
      })}
    </div>
  );
}

/**
 * Never renders a zero it does not mean.
 *
 * Showing 0 while a refetch is in flight and then 14 reads as "nothing to do"
 * for a moment on every filter change — and that moment is exactly when
 * someone glances at the tab. With keepPreviousData the previous count stays
 * on screen; only the very first load has nothing to show, and that gets a
 * skeleton rather than a number.
 */
function StageCount({
  value,
  isLoading,
  active,
}: {
  value: number | undefined;
  isLoading: boolean;
  active: boolean;
}) {
  if (value === undefined) {
    return isLoading ? (
      <span
        className="inline-block h-4 w-6 animate-pulse rounded bg-gray-200"
        aria-label="Loading count"
      />
    ) : null;
  }

  return (
    <span
      className={cn(
        'rounded-full px-2 py-0.5 text-xs tabular-nums',
        active ? 'bg-blue-100 text-blue-900' : 'bg-gray-100 text-gray-600',
        // Stale while a refetch is in flight — dimmed rather than replaced.
        isLoading && 'opacity-60',
      )}
    >
      {value}
    </span>
  );
}
